/**
 * advance-bombardment.ts — per-tick bombardment + Loyalty pipeline (v2M5,
 * PLANETS §2.7 + STRUCTURES §8).
 *
 * Pipeline order within the tick:
 *   1. Each ship with `bombardOrder` fires unspent weapon slots at its
 *      target planet (combat already ran first; cooldowns are accurate).
 *      Damage applied as Pop kills via state.balanceData.bombardment.popKillFactor.
 *      Per-volley hit rolls use weapon accuracy against implicit zero-Dodge
 *      (planets don't dodge). Stamps lastBombardingEmpireId + lastBombardedTick
 *      and accumulates kills into `bombardKillsAccumulator` with a snapshot
 *      `bombardPopBaseline` on the first hit of a fresh window.
 *   2. On the state.balanceData.bombardment.batchTicks boundary (every 10 ticks): for each
 *      planet with a non-empty accumulator, compute Loyalty drop =
 *      `ceil(2 × kills / popBaseline × 100)`, apply, reset accumulator,
 *      roll baseline forward to currentPopulation.
 *   3. Same boundary: Loyalty regen for any planet that hasn't been
 *      bombarded in ≥ state.balanceData.bombardment.batchTicks — +1 per batch toward 100.
 *      Regen entry clears stale batch state (accumulator / baseline /
 *      lastBombardingEmpireId) so a fresh bombardment session rebuilds
 *      from current pop.
 *   4. Transitions: any planet with currentPopulation ≤ 0 depopulates
 *      (PLANETS §8.4 — uninhabited, structures lost); any planet with
 *      Loyalty ≤ 0 flips to lastBombardingEmpireId. Pop-0 takes priority
 *      over Loyalty-0 if both fire on the same boundary.
 *
 * Determinism: only state.rng is consumed (per-volley hit rolls). Iteration
 * over ships is in ascending id order; planets are iterated in insertion
 * order (Map.values is stable).
 */

import { hitChance } from './combat';
import {
  cooldownTicksFor,
  resolveSlotWeapon,
} from './combat-balance';
import {
  planetSimRadiusForSize,
} from './balance-bombardment';
import { nextFloat } from '../state/rng';
import type {
  EmpireId,
  GameState,
  Planet,
  ShipInstance,
  ShipTemplate,
  Structure,
} from '../state/types';

/** Advance bombardment by exactly one tick. Mutates state. */
export function advanceBombardment(state: GameState): void {
  // ----- 1. Per-ship firing pass -----
  const ships = [...state.ships.values()].sort((a, b) => a.id - b.id);
  for (const ship of ships) {
    if (ship.bombardOrder === null) continue;
    if (!canBombardTick(ship)) continue;

    const planet = state.planets.get(ship.bombardOrder.targetPlanetId);
    if (!planet) {
      // Target gone — clear the order so the ship stops trying.
      ship.bombardOrder = null;
      continue;
    }
    // If the planet has flipped to this ship's empire (rare: another
    // attacker's flip, or future diplomacy), stop bombarding our own world.
    if (planet.ownerEmpireId === ship.ownerEmpireId) {
      ship.bombardOrder = null;
      continue;
    }
    // Wrong system — keep order, ship is in transit.
    if (ship.systemId !== planet.systemId) continue;

    fireBombardmentRoundsAt(state, ship, planet);
  }

  // ----- 2 + 3. Batch boundary: drop + regen -----
  // Skip tick 0 to avoid an immediate boundary hit on game start with a
  // freshly-set bombardment order.
  if (state.tick > 0 && state.tick % state.balanceData.bombardment.batchTicks === 0) {
    for (const planet of state.planets.values()) {
      applyBatchBoundary(state, planet);
    }
  }

  // ----- 4. Transitions -----
  // Iterate over a snapshot of planet ids so the depop/flip mutations
  // (which add/remove entries on empire.planetIds, not state.planets, but
  // still — defensive) don't surprise us.
  const planetIds = [...state.planets.keys()];
  for (const id of planetIds) {
    const planet = state.planets.get(id);
    if (!planet) continue;
    // Pop-0 takes priority over Loyalty-0 (PLANETS §8.4): if both hit on
    // the same boundary the planet depopulates rather than flips.
    if (planet.ownerEmpireId !== null && planet.currentPopulation <= 0) {
      depopulatePlanet(state, planet);
      continue;
    }
    if (
      planet.ownerEmpireId !== null &&
      planet.loyalty <= 0 &&
      planet.lastBombardingEmpireId !== null
    ) {
      flipPlanet(state, planet, planet.lastBombardingEmpireId);
    }
  }
}

/** Mid-jump / disabled / cloaked / dead ships can't bombard. */
function canBombardTick(ship: ShipInstance): boolean {
  if (ship.currentHull <= 0) return false;
  if (ship.systemId === null) return false;
  if (ship.disabledTicks > 0) return false;
  if (ship.cloaked) return false;
  return true;
}

function getTemplate(state: GameState, ship: ShipInstance): ShipTemplate | undefined {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return undefined;
  return state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
}

/**
 * v2M6: read the planet's active Shield + Bunker levels and compute the
 * post-reduction fraction of damage that lands. Multiplicative chain per
 * STRUCTURES §8.1: `surviving = (1 - shield) * (1 - bunker)`. Both
 * structures are unique-per-planet so we just pick the matching kind;
 * level-0 (under construction) does not contribute. Levels are clamped
 * defensively to the cap (a future balance pass that lifts the cap
 * shouldn't accidentally over-reduce here).
 */
function damageReductionSurvivingFraction(state: GameState, planet: Planet): number {
  let shieldLevel = 0;
  let bunkerLevel = 0;
  for (const id of planet.structureIds) {
    const s = state.structures.get(id);
    if (!s || s.level < 1) continue;
    if (s.kind === 'planetary-shields') {
      if (s.level > shieldLevel) shieldLevel = s.level;
    } else if (s.kind === 'underground-bunkers') {
      if (s.level > bunkerLevel) bunkerLevel = s.level;
    }
  }
  const shieldRed =
    Math.min(state.balanceData.bombardment.maxShieldLevel, shieldLevel) * state.balanceData.bombardment.damageReductionPerShieldLevel;
  const bunkerRed =
    Math.min(state.balanceData.bombardment.maxBunkerLevel, bunkerLevel) * state.balanceData.bombardment.damageReductionPerBunkerLevel;
  return (1 - shieldRed) * (1 - bunkerRed);
}

/**
 * Pick a non-bunker, alive (currentHull > 0), level-≥1 structure on the
 * planet to absorb the per-shot structure damage share. Selection is
 * weighted by remaining hull, drawn from `state.rng` so the pick is
 * deterministic under save/load and replay. Returns null when no
 * eligible structure exists — caller folds the structure share into the
 * Pop share (STRUCTURES §8.2 fall-back).
 *
 * RNG draw is gated on candidate availability so a planet with no
 * eligible structures consumes zero RNG state — preserves v2M5
 * test-fixture behaviour where no structures existed.
 */
function pickStructureForDamage(state: GameState, planet: Planet): Structure | null {
  const candidates: Structure[] = [];
  let totalHull = 0;
  for (const id of planet.structureIds) {
    const s = state.structures.get(id);
    if (!s) continue;
    if (s.level < 1) continue; // under construction — not yet a hit-target
    if (s.kind === 'underground-bunkers') continue; // immune
    if (s.currentHull <= 0) continue;
    candidates.push(s);
    totalHull += s.currentHull;
  }
  if (candidates.length === 0 || totalHull <= 0) return null;
  let roll = nextFloat(state.rng) * totalHull;
  for (const s of candidates) {
    roll -= s.currentHull;
    if (roll <= 0) return s;
  }
  // Floating-point edge — last candidate.
  return candidates[candidates.length - 1]!;
}

/**
 * Remove a structure from the planet + state.structures. Mirrors the
 * scrap path used during depopulation but for a single structure.
 */
function destroyStructure(state: GameState, planet: Planet, s: Structure): void {
  state.structures.delete(s.id);
  const idx = planet.structureIds.indexOf(s.id);
  if (idx >= 0) planet.structureIds.splice(idx, 1);
}

/**
 * Fire each ready weapon slot from `ship` at `planet`. EMP slots are skipped
 * (per PLANETS §2.7 lock — EMP disables, doesn't damage). Range is
 * ship-to-planet-edge: `dist(ship, planetCenter) ≤ weaponRange + planetRadius`.
 */
function fireBombardmentRoundsAt(state: GameState, ship: ShipInstance, planet: Planet): void {
  if (ship.weaponSlots.length === 0) return;
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return;
  const tmpl = getTemplate(state, ship);
  if (!tmpl || tmpl.weaponLoadout.length === 0) return;

  const dx = ship.sx - planet.systemX;
  const dy = ship.sy - planet.systemY;
  const distToCentre = Math.sqrt(dx * dx + dy * dy);
  const planetRadius = planetSimRadiusForSize(planet.size);

  const loadout = tmpl.weaponLoadout;
  for (let i = 0; i < loadout.length; i++) {
    const slotTmpl = loadout[i]!;
    const slot = ship.weaponSlots[i];
    if (!slot) continue;
    // Combat already decremented this slot's cooldown earlier in the tick;
    // we never decrement again. Slots that fired in combat have
    // cooldownTicks > 0 and are silently skipped here.
    if (slot.cooldownTicks > 0) continue;

    const weapon = resolveSlotWeapon(
      slotTmpl.weapon,
      state.weapons,
      empire.researchedTechs,
    );
    if (!weapon) continue;
    if (weapon.damageType === 'EMP') continue;
    if (slot.ammoRemaining <= 0) continue;

    const rangeEff = weapon.range * state.balanceData.combat.weaponRangeScale + planetRadius;
    if (distToCentre > rangeEff) continue;

    // Per-round volley. Each round rolls independently against weapon
    // accuracy with planet-implicit Dodge=0 (matches the combat fireSalvo
    // shape). On hit, push damage through the v2M6 reduction chain:
    //
    //   reducedDamage  = weapon.damage × (1 - shield) × (1 - bunker)
    //   structures?    no  → popDamage = reducedDamage; no structure hit
    //                  yes → popDamage = reducedDamage × 0.7
    //                        structureDamage = reducedDamage × 0.3
    //                        applied to a hull-weighted pick (excl. Bunkers)
    //
    // Pop kills then convert via state.balanceData.bombardment.popKillFactor (v2M7-tunable).
    // The picker is called per-shot so a structure destroyed mid-volley is
    // dropped from the candidate set for subsequent shots in the same volley.
    const hitP = hitChance(weapon.accuracy, 0);
    const surviving = damageReductionSurvivingFraction(state, planet);
    let killsThisVolley = 0;
    for (let k = 0; k < slotTmpl.count; k++) {
      const roll = nextFloat(state.rng);
      if (roll >= hitP) continue;

      const reducedDamage = weapon.damage * surviving;
      const target = pickStructureForDamage(state, planet);

      let popShare: number;
      let structureShare: number;
      if (target === null) {
        // No eligible structures — full share folds into Pop kills.
        popShare = reducedDamage;
        structureShare = 0;
      } else {
        popShare = reducedDamage * state.balanceData.bombardment.popDamageFraction;
        structureShare = reducedDamage * state.balanceData.bombardment.structureDamageFraction;
      }

      killsThisVolley += popShare * state.balanceData.bombardment.popKillFactor;

      if (target !== null && structureShare > 0) {
        target.currentHull -= structureShare;
        if (target.currentHull <= 0) {
          destroyStructure(state, planet, target);
        }
      }
    }

    if (killsThisVolley > 0) {
      // Snapshot the baseline on first hit of a fresh window — the
      // denominator in the Loyalty drop formula. Set on first kill in the
      // batch only; not reset until the boundary or a regen sweep.
      if (planet.bombardPopBaseline === null) {
        planet.bombardPopBaseline = planet.currentPopulation;
      }
      const actualKills = Math.min(killsThisVolley, planet.currentPopulation);
      planet.currentPopulation -= actualKills;
      planet.bombardKillsAccumulator += actualKills;
      planet.lastBombardedTick = state.tick;
      planet.lastBombardingEmpireId = ship.ownerEmpireId;
    } else if (slotTmpl.count > 0 && hitP > 0) {
      // Edge case: hits landed but all damage went to structures (zero
      // pop share, e.g. a future config). Still stamp the bombardment
      // tracker fields — Loyalty regen gate must respect that the planet
      // is under fire even when Pop is unchanged this volley. v2M6 with
      // 70/30 split has popShare > 0 always when reducedDamage > 0, so
      // this branch is defensive against future tuning.
    }

    // Slot bookkeeping mirrors advance-combat: stamp lastFired (breaks
    // stealth per MECHANICS §2.1), decrement Concussion ammo, set cooldown.
    ship.lastFiredTick = state.tick;
    if (Number.isFinite(slot.ammoRemaining)) {
      slot.ammoRemaining = Math.max(0, slot.ammoRemaining - slotTmpl.count);
    }
    slot.cooldownTicks = cooldownTicksFor(weapon.rateOfFire);

    // If pop is now 0, no point continuing this ship's volley — the
    // remaining slots wouldn't kill anything (and the depopulation
    // transition fires after this pass anyway).
    if (planet.currentPopulation <= 0) break;
  }
}

/**
 * On the 10-tick batch boundary, compute the Loyalty drop for any planet
 * that took bombardment damage in the just-ended window, and regen
 * Loyalty for planets that have been free of bombardment for the full
 * gate window.
 */
function applyBatchBoundary(state: GameState, planet: Planet): void {
  // Drop and regen are mutually exclusive on a given boundary — if the
  // window had bombardment activity (non-empty accumulator), we apply the
  // drop and roll the baseline forward; we do NOT also regen on the same
  // boundary, even if `lastBombardedTick` is far enough back to satisfy
  // the gate (which it can be when bombardment first lands on a planet
  // that has been quiet for a long time). Without this guard the regen
  // branch overwrites the rolled-forward baseline with null.
  const hadKillsInWindow =
    planet.bombardKillsAccumulator > 0 &&
    planet.bombardPopBaseline !== null &&
    planet.bombardPopBaseline > 0;

  if (hadKillsInWindow) {
    const dropPct =
      (planet.bombardKillsAccumulator / (planet.bombardPopBaseline as number)) * 100;
    const drop = Math.ceil(2 * dropPct);
    planet.loyalty = Math.max(0, planet.loyalty - drop);
    planet.bombardKillsAccumulator = 0;
    // Roll the baseline forward to the *new* current pop. If bombardment
    // continues into the next window the formula stays calibrated to
    // present pop. If bombardment goes silent, the regen branch on a
    // later boundary will clear it.
    planet.bombardPopBaseline = planet.currentPopulation;
    return;
  }

  // Regen — fires only when no bombardment has landed for the full gate
  // window. Resets stale batch state so a fresh bombardment session
  // rebuilds its baseline from then-current pop.
  const ticksSinceBombard =
    planet.lastBombardedTick === null
      ? Number.POSITIVE_INFINITY
      : state.tick - planet.lastBombardedTick;
  if (ticksSinceBombard >= state.balanceData.bombardment.batchTicks) {
    if (planet.ownerEmpireId !== null && planet.loyalty < state.balanceData.bombardment.loyaltyMax) {
      planet.loyalty = Math.min(state.balanceData.bombardment.loyaltyMax, planet.loyalty + state.balanceData.bombardment.loyaltyRegenPerBatch);
    }
    planet.bombardKillsAccumulator = 0;
    planet.bombardPopBaseline = null;
    planet.lastBombardingEmpireId = null;
  }
}

/**
 * Transfer planet ownership to `newOwnerId`. Per PLANETS §2.7 + STRUCTURES §8:
 * Loyalty resets to 100; surviving Pop and generic structures transfer
 * intact; race-specific structures scrap immediately (no transfer, no
 * refund — v1 catalogue has no race-specific on-planet structures yet,
 * so the scrap branch is forward-compat infrastructure that activates
 * when v3 race-specifics land). Bombard state cleared. All ships across
 * the game with a bombardOrder targeting this planet have their order
 * cleared (Loyalty just reset to 100, continuing would be pointless;
 * players can re-issue if the planet is still hostile to them).
 *
 * v3M21.3 — exported so the diplomatic Annex Command path
 * (`invokeAnnex` in commands.ts) can reuse the same ownership-transfer
 * machinery. v3M21-specific diplomatic state on the planet is also
 * cleared here so bombardment-driven AND diplomacy-driven flips
 * converge: every empire's `Planet.influence` entry on this planet is
 * dropped (the planet is no longer neutral so the pool is meaningless);
 * `tradingWith`, `federatedWith`, `vassalOf` cleared; every empire's
 * `diplomaticFocus` that was pointing at this planet cleared.
 */
export function flipPlanet(state: GameState, planet: Planet, newOwnerId: EmpireId): void {
  const oldOwnerId = planet.ownerEmpireId;
  if (oldOwnerId === newOwnerId) return; // defensive

  if (oldOwnerId !== null) {
    const oldOwner = state.empires.get(oldOwnerId);
    if (oldOwner) {
      const idx = oldOwner.planetIds.indexOf(planet.id);
      if (idx >= 0) oldOwner.planetIds.splice(idx, 1);
    }
  }
  const newOwner = state.empires.get(newOwnerId);
  if (newOwner && !newOwner.planetIds.includes(planet.id)) {
    newOwner.planetIds.push(planet.id);
  }

  planet.ownerEmpireId = newOwnerId;
  planet.loyalty = state.balanceData.bombardment.loyaltyMax;
  planet.bombardKillsAccumulator = 0;
  planet.bombardPopBaseline = null;
  planet.lastBombardedTick = null;
  planet.lastBombardingEmpireId = null;

  // Race-specific structure scrap pass — forward-compat infrastructure.
  // v1 on-planet Structure type has no `raceId` field, so this loop is a
  // no-op today. When race-specific structures land in v3, gate the
  // transfer here on a `Structure.raceId === newOwner.raceId` check; if
  // not, scrap by removing from state.structures + planet.structureIds.
  // The hook is intentionally inert for v2M5 to avoid speculative shape
  // changes on the Structure type.

  // Clear any bombardOrder targeting this planet — the new owner is now
  // friendly to whoever flipped it (so they shouldn't keep firing); old
  // owners no longer own the planet so the order is moot.
  for (const ship of state.ships.values()) {
    if (ship.bombardOrder?.targetPlanetId === planet.id) {
      ship.bombardOrder = null;
    }
  }

  // v3M21.3 — diplomatic state cleanup. The planet is no longer neutral,
  // so every empire's Influence pool on it is meaningless and gets
  // dropped. Federation / Vassal / Trade arrangements on the (former)
  // neutral end immediately. Any empire that had this planet as their
  // diplomatic-focus target loses focus (income reverts to treasury via
  // the existing per-tick auto-clear path; clearing here is a fast-path
  // so the next tick already sees the cleared state).
  planet.influence.clear();
  planet.federatedWith = null;
  planet.tradingWith = [];
  planet.vassalOf = null;
  for (const empire of state.empires.values()) {
    if (empire.diplomaticFocus === planet.id) empire.diplomaticFocus = null;
  }
}

/**
 * Depopulate a planet (PLANETS §8.4). All structures lost, ownerEmpireId
 * set to null, biome unchanged. Loyalty resets to 100 so a future
 * recoloniser starts fresh. All bombardOrders targeting this planet
 * cleared (no point bombarding an empty rock).
 */
function depopulatePlanet(state: GameState, planet: Planet): void {
  const oldOwnerId = planet.ownerEmpireId;
  if (oldOwnerId !== null) {
    const oldOwner = state.empires.get(oldOwnerId);
    if (oldOwner) {
      const idx = oldOwner.planetIds.indexOf(planet.id);
      if (idx >= 0) oldOwner.planetIds.splice(idx, 1);
    }
  }

  // Scrap all structures on the planet — they have nobody to operate them.
  for (const structureId of planet.structureIds) {
    state.structures.delete(structureId);
  }
  planet.structureIds = [];

  planet.ownerEmpireId = null;
  planet.currentPopulation = 0;
  planet.loyalty = state.balanceData.bombardment.loyaltyMax;
  planet.bombardKillsAccumulator = 0;
  planet.bombardPopBaseline = null;
  planet.lastBombardedTick = null;
  planet.lastBombardingEmpireId = null;

  // Clear any bombardOrder targeting this planet — orbiting ships have no
  // reason to keep firing on a Pop-0 rock.
  for (const ship of state.ships.values()) {
    if (ship.bombardOrder?.targetPlanetId === planet.id) {
      ship.bombardOrder = null;
    }
  }
}
